import { action, computed, observable } from 'mobx';

import { FeedbackMenuItemAnalytics } from 'http/Api/Feedback/types';
import Log from 'helpers/log';
import httpFacade from 'http/httpFacade';
import { PromiseService } from 'services/PromiseService';
import { getFormattedDate, SHORT_DATE_FORMAT } from 'helpers/datetime';

import { AvgRatingChartData, RatingCountChartData } from './types';

class FeedbackMenuItemAnalyticsModalStore {
  @observable data: FeedbackMenuItemAnalytics | null = null;

  @observable private _promiseService = new PromiseService();

  @action
  fetchAnalytics = async (id: string, startDate: string, endDate: string) => {
    try {
      const { data } = await this._promiseService.add(() =>
        httpFacade.feedback.fetchMenuItemAnalytics({
          id,
          startDate,
          endDate,
        }),
      );

      this.data = data;
    } catch (e) {
      Log.info(e);
    }
  };

  @computed
  get ratingCountChartData() {
    const { ratingCountPointDtos } = this.data || {};

    const data: RatingCountChartData[] = [];
    const maxRating = 5;

    for (let i = 1; i <= maxRating; i++) {
      const count =
        ratingCountPointDtos?.find(({ rating }) => rating === i)?.count ?? 0;

      data.push({ rating: i, count });
    }

    return data;
  }

  @computed
  get avgRatingChartData() {
    const data: AvgRatingChartData[] = [];

    const { ratingTrendPointDto } = this.data || {};
    if (!ratingTrendPointDto?.length) return data;

    for (const ratingTrend of ratingTrendPointDto) {
      const { bucketStart, bucketEnd, avgPercent } = ratingTrend;
      if (bucketStart === bucketEnd) {
        data.push({
          avgPercent,
          date: getFormattedDate(bucketStart, SHORT_DATE_FORMAT),
        });

        continue;
      }

      data.push({
        avgPercent,
        date: `${getFormattedDate(
          bucketStart,
          SHORT_DATE_FORMAT,
        )}-${getFormattedDate(bucketEnd, SHORT_DATE_FORMAT)}`,
      });
    }

    return data;
  }

  @computed
  get loading() {
    return !this._promiseService.isCompleted || this._promiseService.isPending;
  }
}

export default FeedbackMenuItemAnalyticsModalStore;
